import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for intersection observer with debouncing
 * @param {Object} options - Intersection observer options
 * @param {number} debounceMs - Debounce delay in milliseconds
 * @returns {Array} - [ref, isIntersecting, intersectionRatio]
 */
const useIntersectionObserver = (options = {}, debounceMs = 100) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [intersectionRatio, setIntersectionRatio] = useState(0);
  const elementRef = useRef(null);
  const observerRef = useRef(null);
  const debounceRef = useRef(null);

  const {
    threshold = [0, 0.25, 0.5, 0.75, 1],
    rootMargin = '50px',
    root = null
  } = options;

  useEffect(() => {
    const element = elementRef.current;
    
    if (!element) return;

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        
        // Clear existing debounce
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        // Debounce the intersection changes
        debounceRef.current = setTimeout(() => {
          setIsIntersecting(entry.isIntersecting);
          setIntersectionRatio(entry.intersectionRatio);
        }, debounceMs);
      },
      {
        threshold,
        rootMargin,
        root
      }
    );

    observer.observe(element);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [threshold, rootMargin, root, debounceMs]);

  return [elementRef, isIntersecting, intersectionRatio];
};

export default useIntersectionObserver;